// provisionalNodeUtils.js
//
// Pure helpers for provisional create stubs (no Firestore imports).

import { IGNORED_ACTIVITY_FIELD_ROOTS } from 'node/activity/utils/placeholderTitleRename';

export const IGNORED_PROVISIONAL_PATCH_ROOTS = new Set([
  ...IGNORED_ACTIVITY_FIELD_ROOTS,
  'provisional',
  'key',
  'type',
  'module',
  'profileRoot',
  'parentTitle',
  'parentType',
  'userKey',
  'baseKey',
  'searchIndex',
  'tagIndex',
  'updatedBy',
  'updatedByUser',
  'dateCreated',
  'dateModified',
  'dateUpdated',
]);

/**
 * Stable JSON for deep equality of seed vs patch values.
 *
 * @param {unknown} value
 * @returns {string}
 */
export function stableSerialize(value) {
  if (value === undefined) return 'undefined';
  if (value === null) return 'null';
  if (typeof value !== 'object') return JSON.stringify(value);

  if (Array.isArray(value)) {
    return `[${value.map((entry) => stableSerialize(entry)).join(',')}]`;
  }

  // Firestore Timestamp / Date
  if (typeof value?.toMillis === 'function') {
    return `ts:${value.toMillis()}`;
  }
  if (value instanceof Date) {
    return `ts:${value.getTime()}`;
  }

  const keys = Object.keys(value).sort();
  return `{${keys.map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`).join(',')}}`;
}

/**
 * @param {unknown} left
 * @param {unknown} right
 * @returns {boolean}
 */
export function valuesEqual(left, right) {
  return stableSerialize(left) === stableSerialize(right);
}

/**
 * Snapshot of user-facing fields used as the create seed baseline.
 *
 * @param {object|null|undefined} node
 * @returns {object}
 */
export function buildProvisionalSeed(node) {
  if (!node || typeof node !== 'object') return {};

  const seed = {};
  Object.keys(node).forEach((key) => {
    if (IGNORED_PROVISIONAL_PATCH_ROOTS.has(key)) return;
    seed[key] = node[key];
  });
  return seed;
}

/**
 * True when the node is still marked provisional on the document.
 *
 * @param {object|null|undefined} node
 * @returns {boolean}
 */
export function isProvisional(node) {
  return node?.provisional === true;
}

/**
 * True when a write patch changes user-facing fields vs the baseline seed
 * (or vs `fallbackBaseline` when no session seed exists).
 *
 * @param {object|null|undefined} patch
 * @param {object|null|undefined} seed
 * @param {object|null|undefined} [fallbackBaseline] - Pre-patch node when seed missing.
 * @returns {boolean}
 */
export function isMeaningfulEdit(patch, seed, fallbackBaseline = null) {
  if (!patch || typeof patch !== 'object') return false;

  const baseline = (seed && typeof seed === 'object')
    ? seed
    : (fallbackBaseline && typeof fallbackBaseline === 'object' ? fallbackBaseline : null);

  if (!baseline) return false;

  const keys = Object.keys(patch);
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    if (IGNORED_PROVISIONAL_PATCH_ROOTS.has(key)) continue;

    const nextValue = patch[key];
    const baselineValue = baseline[key];

    if (nextValue && typeof nextValue === 'object' && !Array.isArray(nextValue)
      && !(typeof nextValue?.toMillis === 'function')
      && !(nextValue instanceof Date)
      && baselineValue && typeof baselineValue === 'object' && !Array.isArray(baselineValue)
    ) {
      // Nested data_* (or similar): compare only keys present in the patch object.
      const nestedKeys = Object.keys(nextValue);
      for (let j = 0; j < nestedKeys.length; j += 1) {
        const nestedKey = nestedKeys[j];
        if (!valuesEqual(nextValue[nestedKey], baselineValue[nestedKey])) {
          return true;
        }
      }
      continue;
    }

    if (!valuesEqual(nextValue, baselineValue)) {
      return true;
    }
  }

  return false;
}
